/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, ElementRef, HostListener, OnDestroy, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {StateService, UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import template from './global-search.component.html';
import {SearchApiService} from '../common/controller/search-api.service';
import {objectTypeStateParams} from '../common/filters/object-type-state-params';
import {updateSearchQueryParams} from './search.util';
import {APP_SHARED} from '../common/app-shared';

@Component({
  standalone: true,
  selector: 'adn-global-search',
  imports: [...APP_SHARED, FormsModule],
  template
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  term: string;
  private readonly resultsState = signal<any[]>([]);
  private readonly dropdownOpenState = signal(false);
  private readonly activeIndexState = signal(-1);

  get results(): any[] { return this.resultsState(); }
  set results(value: any[]) { this.resultsState.set(value); }
  get dropdownOpen(): boolean { return this.dropdownOpenState(); }
  set dropdownOpen(value: boolean) { this.dropdownOpenState.set(value); }
  get activeIndex(): number { return this.activeIndexState(); }
  set activeIndex(value: number) { this.activeIndexState.set(value); }

  private searchTimeout: any;
  private blurTimeout: any;
  private pendingRequest = 0;
  private deregisterTransitions: () => void;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly searchApi: SearchApiService,
    private readonly $state: StateService,
    private readonly uiRouter: UIRouter
  ) {
  }

  ngOnInit(): void {
    this.deregisterTransitions = this.uiRouter.transitionService.onSuccess({}, () => this.closeDropdown()) as () => void;
  }

  ngOnDestroy(): void {
    clearTimeout(this.searchTimeout);
    clearTimeout(this.blurTimeout);
    this.pendingRequest++;
    if (this.deregisterTransitions) {
      this.deregisterTransitions();
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    this.$state.go('app.search', updateSearchQueryParams({q: null, types: null}, this.term), {inherit: false}).then(() => {
      this.blurHost();
    });
    this.term = null;
    this.closeDropdown();
  }

  onTermChange(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.loadResults(this.term);
    }, 150);
  }

  openDropdown(): void {
    clearTimeout(this.blurTimeout);
    this.dropdownOpen = true;
    if (this.term) {
      this.loadResults(this.term);
    }
  }

  onInputBlur(): void {
    clearTimeout(this.blurTimeout);
    this.blurTimeout = setTimeout(() => {
      this.closeDropdown();
    }, 150);
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (!this.dropdownOpen || !this.results.length) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.onSubmit(event);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, this.results.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, -1);
        break;
      case 'Enter':
        event.preventDefault();
        if (this.activeIndex >= 0 && this.activeIndex < this.results.length) {
          this.selectItem(this.results[this.activeIndex]);
        } else {
          this.onSubmit(event);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.closeDropdown();
        break;
      default:
        break;
    }
  }

  selectItem(item: any): void {
    this.$state.go(_.get(item, 'objectType.stateName') || 'app.search', objectTypeStateParams(item), {inherit: false}).then(() => {
      this.blurHost();
    });
    this.term = null;
    this.closeDropdown();
  }

  private loadResults(term: string): void {
    const requestId = ++this.pendingRequest;
    const paramObject = updateSearchQueryParams({
      q: null,
      types: null,
      pageSize: 10
    }, term);

    this.searchApi.search(paramObject).subscribe((data) => {
      if (requestId !== this.pendingRequest) {
        return;
      }
      const results = [...(data.searchResults || [])];
      results.push({
        name: "See full results for '" + term + "'",
        objectType: {
          name: 'Search',
          icon: 'fa-search',
          stateName: 'app.search',
          stateParams: paramObject
        }
      });
      if (requestId !== this.pendingRequest) {
        return;
      }
      this.results = results;
      this.activeIndex = -1;
      this.dropdownOpen = true;
    });
  }

  private closeDropdown(): void {
    clearTimeout(this.searchTimeout);
    this.pendingRequest++;
    this.dropdownOpen = false;
    this.results = [];
    this.activeIndex = -1;
  }

  private blurHost(): void {
    const input = this.elementRef.nativeElement.querySelector('input');
    if (input) {
      input.blur();
    }
  }
}
